/**
 * React Query hooks over the backend notification feed, mapped into the
 * shared `Notification` shape the NotificationDrawer already renders.
 *
 * The Zustand `notificationStore` remains for CLIENT-pushed ephemeral
 * events (datasheet extraction done, upload diffs); this hook adds the
 * SERVER-persisted billing/account alerts. The drawer merges both.
 */
import { ApiError } from "@lib/axios";
import type {
  Notification,
  NotificationSeverity,
} from "@stores/notificationStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "./api";
import type { NotificationKind, ServerNotification } from "./api";

/** A drawer row that knows where it came from, so mark-read routes to the
 *  backend (server) or the local store (client). */
export type FeedNotification = Notification & {
  origin: "server" | "client";
  /** Raw backend id (no prefix) — present only for server rows. */
  serverId?: string;
};

export const notificationKeys = {
  all: ["notifications"] as const,
  list: () => [...notificationKeys.all, "list"] as const,
};

const KIND_SEVERITY: Record<string, NotificationSeverity> = {
  payment_failed: "error",
  balance_depleted: "error",
  low_balance: "warning",
  payment_recovered: "success",
};

function kindToSeverity(kind: NotificationKind): NotificationSeverity {
  return KIND_SEVERITY[kind] ?? "info";
}

/** Map a backend notification into the drawer's row shape. The action
 *  navigates to the CTA target (an internal path like /settings/billing);
 *  `onClick` is filled in by the drawer, which owns navigation + closing. */
export function toFeedNotification(
  n: ServerNotification,
  onAction: (url: string) => void,
): FeedNotification {
  return {
    id: `srv:${n.id}`,
    serverId: n.id,
    origin: "server",
    ts: n.created_at ?? new Date(0).toISOString(),
    severity: kindToSeverity(n.kind),
    title: n.title,
    body: n.body || undefined,
    seen: n.read_at != null,
    action: n.action_url
      ? {
          label: n.action_label || "Open",
          onClick: () => onAction(n.action_url as string),
        }
      : undefined,
  };
}

/** Poll the server feed. Auth-gated: a 401 on a logged-out page must not
 *  spin — React Query stops retrying and the drawer simply shows nothing
 *  from the server. */
export function useServerNotifications() {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: notificationsApi.list,
    // Billing alerts aren't time-critical to the second — a 60s poll keeps
    // the bell fresh without hammering the API. Refetch on focus catches
    // the "came back to the tab after a failed payment" case promptly.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: (count, err) => {
      // Don't retry auth failures (logged-out / probing) — they won't
      // recover without a login and would busy-loop the endpoint.
      if (
        err instanceof ApiError &&
        (err.status === 401 || err.status === 403)
      ) {
        return false;
      }
      return count < 2;
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serverId: string) => notificationsApi.markRead(serverId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: notificationKeys.list() }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: notificationKeys.list() }),
  });
}
